/**
 * Get-Your-Answer — Library API client (MongoDB-backed search).
 *
 * Wraps the new /api/v1/get-your-answer/* endpoints introduced in SEO-013.
 * The library page should use these — NOT the static JSON file — because:
 *   - Searches log to gya_query_log so the AI expander can learn from real queries
 *   - AI-generated phrasings (status=published) start showing up automatically
 *   - Rate limiting + honeypot run server-side
 *
 * All functions fail-soft: on error they resolve with empty results rather than
 * throwing, so the UI stays usable.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// In-flight request deduper. React StrictMode double-fires effects in dev,
// and the rate limiter (10 req/min/IP) trips quickly if we send a duplicate
// request for each mount. Identical concurrent requests share the same
// promise so the browser only hits the network once per unique payload.
const _inflight = new Map();

// Topics are essentially static (only change after admin approves AI questions)
// so we cache them at the module level for the lifetime of the SPA session.
let _topicsCache = null;
let _topicsCachePromise = null;

/**
 * Public search.
 * @param {string} query - user-typed text (empty allowed)
 * @param {Object} opts
 *   @param {string} opts.topic    - topic_id filter (e.g. "irs_notices") or "all"
 *   @param {string} opts.urgency  - "high"|"medium"|"low" or "all"
 *   @param {number} opts.limit    - 1..100, default 30
 * @returns {Promise<{results: Array, total: number, took_ms: number, query: string, error?: string}>}
 */
export async function searchLibrary(query, opts = {}) {
  const payload = {
    query: (query || "").slice(0, 300),
    topic: opts.topic && opts.topic !== "all" ? opts.topic : null,
    urgency: opts.urgency && opts.urgency !== "all" ? opts.urgency : null,
    limit: opts.limit || 30,
  };
  const key = JSON.stringify(payload);
  if (_inflight.has(key)) return _inflight.get(key);

  const promise = (async () => {
    try {
      const res = await fetch(`${BACKEND}/api/v1/get-your-answer/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.status === 429) {
        return { results: [], total: 0, took_ms: 0, query, error: "rate_limited" };
      }
      if (!res.ok) {
        return { results: [], total: 0, took_ms: 0, query, error: "search_failed" };
      }
      return await res.json();
    } catch (e) {
      return { results: [], total: 0, took_ms: 0, query, error: "network_error" };
    } finally {
      // Tiny grace period before clearing the in-flight slot — gives
      // StrictMode's second invocation enough time to land on the same promise.
      setTimeout(() => _inflight.delete(key), 50);
    }
  })();
  _inflight.set(key, promise);
  return promise;
}

/**
 * Topic filter chips for the library — includes canonical_count per topic.
 * Module-level cached: topics don't change between pages.
 */
export async function listTopics() {
  if (_topicsCache) return _topicsCache;
  if (_topicsCachePromise) return _topicsCachePromise;
  _topicsCachePromise = (async () => {
    try {
      const res = await fetch(`${BACKEND}/api/v1/get-your-answer/topics`);
      if (!res.ok) {
        _topicsCachePromise = null;
        return { topics: [], canonical_total: 0 };
      }
      const data = await res.json();
      _topicsCache = data;
      return data;
    } catch {
      _topicsCachePromise = null;
      return { topics: [], canonical_total: 0 };
    }
  })();
  return _topicsCachePromise;
}

/**
 * Capture a user-described question when our search doesn't match. Feeds the
 * AI expander's reactive generation pipeline so future searches will find it.
 */
export async function describeQuestion(description, email = null) {
  try {
    const res = await fetch(`${BACKEND}/api/v1/get-your-answer/describe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ description, email }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Question detail (slug → full question doc).
 */
export async function fetchQuestion(slug) {
  try {
    const res = await fetch(
      `${BACKEND}/api/v1/get-your-answer/question/${encodeURIComponent(slug)}`
    );
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Increment click-through analytics on a question (fire-and-forget).
 */
export function trackQuestionClick(slug) {
  try {
    const url = `${BACKEND}/api/v1/get-your-answer/track-click?slug=${encodeURIComponent(slug)}`;
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([""], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", keepalive: true }).catch(() => {});
    }
  } catch {
    /* no-op */
  }
}
